// @workflow platform.viewer
/**
 * The app's wiring of the viewer adapter to today's implementation. With src/platform/viewer/
 * as a whole, this is the ONLY place allowed to import forgeService.js / forgeviewer.js
 * (tools/check-legacy-imports.mjs enforces it). When a capability's logic moves out of
 * forgeService, its dependency here changes and the adapter's callers do not.
 */
import Vue from 'vue';
import { takeScreenshot } from '@/scripts/js/forgeService';
import {
  attachmentExtension,
  isPdfViewer,
  isImageViewer,
  pdfViewerOpenPageByNumber,
  renderedLinkTagIds,
  highlightTagLinkElements,
  ensureDocLinkVisible,
  TurnViewerLockOff,
} from '@/scripts/js/forgeviewer';
import { setPermeativeElementsTheme, getBBBox } from '@/utils/forgeUtils';
import { $hexToVector4 } from '@/utils';
import { useSpatialFocus } from '@/features/Dashboard/composables/useSpatialFocus';
import dashboardWidgetsService from '@/features/Dashboard/service/dashboard-widgets.service';
import { buildSpatialRenderPlan } from '@/features/Dashboard/spatialFocus/spatialFocusArbitrator';
import { listLoadedModels, fitSpatialRenderPlan } from '@/features/Dashboard/spatialFocus/forgeSpatialFocusAdapter';
import { viewerPanes } from './panes.js';

/** forgeService's own toast for "no 3D viewer" (forgeService.js:2783). */
const WARNING_OPTIONS = Object.freeze({ position: 'top-right', timeout: 3000 });

const notify = Object.freeze({
  warning: (text) => Vue.prototype.$dialog.notify.warning(text, { ...WARNING_OPTIONS }),
});

/** Autodesk's event names, read when the viewer script has loaded (window.Autodesk). */
const events = Object.freeze({
  get OBJECT_TREE_CREATED() {
    return window.Autodesk.Viewing.OBJECT_TREE_CREATED_EVENT;
  },
});

/** The document view's legacy functions (documents.js). */
const documentDeps = Object.freeze({
  isDrawingFile: (fileName) => {
    const ext = attachmentExtension(fileName);
    return isPdfViewer(ext) || isImageViewer(ext);
  },
  openPage: pdfViewerOpenPageByNumber,
  renderedTagIds: renderedLinkTagIds,
  highlightTags: highlightTagLinkElements,
  showLink: ensureDocLinkVisible,
});

/**
 * @param {import('vuex').Store} store
 * @returns {object} the deps of core.js `createViewer`
 */
export function legacyViewerDeps(store) {
  return {
    store,
    panes: viewerPanes,
    // The spatial-focus state is shared per viewer by every caller, so one instance serves all.
    spatialFocus: useSpatialFocus(),
    resolveSpatialFocus: (body, options) => dashboardWidgetsService.resolveSpatialFocus(body, options),
    buildRenderPlan: buildSpatialRenderPlan,
    fitRenderPlan: fitSpatialRenderPlan,
    listLoadedModels,
    themeElements: setPermeativeElementsTheme,
    toThemingColor: $hexToVector4,
    boxOf: getBBBox,
    takeScreenshot,
    notify,
    events,
    unlockNavigation: TurnViewerLockOff,
    documents: documentDeps,
  };
}
